import { Delete } from '@mui/icons-material';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Stack, Typography } from '@mui/material';
import { cloneDeep, get, isArray, set } from 'lodash';
import React, { FC, useEffect, useState } from 'react';
import CreateForm from '../../../../../../../../../components/ui-component/form-component/create-form/CreateForm';
import CreateTable from '../../../../../../../../../components/ui-component/form-component/create-form/CreateTable';
import MUIToolTip from '../../../../../../../../../components/ui-component/mui-tooltip/MUIToolTip';
import { useApplication } from '../../../../../../../../../context/ApplicationContext';
import { getHalbJahrDetail } from '../../../../../../../../../lib/api/antrag';
import DeleteRecord from '../../../../../../../../../lib/api/DeleteRecord';
import GetRecords from '../../../../../../../../../lib/api/GetRecords';
import { APIS } from '../../../../../../../../../lib/constants/apis';
import {
	ANTRAG_HALBJAHR_ART,
	BEWILLIGTE_SPALTE_ANZEIGEN,
	EINZELPOSITION_MAX_ERLAUBT_TEXT,
	FORM_FIELDS_TYPES,
	KOSTEN_ARTEN,
	MAX_COLUMNS_HORIZONTAL_LAYOUT,
	PLATZHALTER_TEXT,
	PROGRAMM_MAX_ERLAUBT_TEXT,
} from '../../../../../../../../../lib/constants/constant';
import { FormStrucItem } from '../../../../../../../../../lib/types/types';
import {
	addIndexesToPath,
	generateUniqueId,
	getFieldNamefromDataPath,
	getIDFromResponseOption,
	getOptionsFromRes,
	removeDataKeys,
	removeNullDeep,
} from '../../../../../../../../../utils/helper';
import ApplicationHeading from '../../../../../../application-heading/ApplicationHeading';

// import schemas
import AntragSchema from '../../../../../../../../../lib/paths/antrag_FULL_Paths.json';
import SparteSchema from '../../../../../../../../../lib/paths/division_FULL_Paths.json';
import FoerderProgrammSchema from '../../../../../../../../../lib/paths/foerderprogramm_FULL_Paths.json';
import KostenPositionSchema from '../../../../../../../../../lib/paths/kostenposition_FULL_Paths.json';
import SchulJahrSchema from '../../../../../../../../../lib/paths/schluljahr_FULL_Paths.json';
import UnternehmenSchema from '../../../../../../../../../lib/paths/unternehmen_FULL_Paths.json';
import { Required } from '../../../../../../../../../lib/validation/ValidationTypes';
import { DisplayEuro } from '../../../../../../../../../utils/formats';
const P = AntragSchema.attributes;
const UN_P = UnternehmenSchema.attributes;
const SP_P = SparteSchema.attributes;
const FP_P = FoerderProgrammSchema.attributes;
const KP_P = KostenPositionSchema.attributes;
const SJ_P = SchulJahrSchema.attributes;

const PersonalKosten: FC<{}> = () => {
	const { application, schulJahrDetails, setApplicationProperty, saveApplication, readOnly, printMode } = useApplication();

	const [allFoerderProgramms, setAllFoerderProgramm] = useState([]);
	const [allSparte, setAllSparte] = useState([]);
	const [allGrundSchule, setAllGrundSchule] = useState([]);
	const [halbJahrDetails, setHalbJahrDetails] = useState<any>();

	const groupedData = getGroupedData(application);
	const isAddProgrammDisabled = readOnly || Object.keys(groupedData).length >= 5;

	useEffect(() => {
		if (!schulJahrDetails) return;

		let halbjahr: any;

		if (get(schulJahrDetails, SJ_P.erste_Halbjahr.schema.attributes.Aktiv.path)) {
			halbjahr = getHalbJahrDetail(schulJahrDetails, ANTRAG_HALBJAHR_ART.ersteHalbJahr);
		} else if (get(schulJahrDetails, SJ_P.zweite_Halbjahr.schema.attributes.Aktiv.path)) {
			halbjahr = getHalbJahrDetail(schulJahrDetails, ANTRAG_HALBJAHR_ART.zweiteHalbJahr);
		}

		if (halbjahr) {
			setHalbJahrDetails(halbjahr);
			if (!groupedData || Object.keys(groupedData).length === 0) {
				handleAddPersonalKosten(halbjahr);
			}
		}
		let appCopy = cloneDeep(application);
		setApplicationProperty(P.Status_Status.path, get(appCopy, P.Status_Status.path, ''));
	}, [schulJahrDetails]);

	const FoerderSumme: Record<string, { foerderprogramm: string; summe: number }> = getFoerderSumme(groupedData);
	const shouldUseVerticalPrintLayout = printMode && columnsLabelForPrint(application).length > MAX_COLUMNS_HORIZONTAL_LAYOUT;
	// const [tableScrollLeft, setTableScrollLeft] = useState(0);

	const handleAddPersonalKosten = async (halbjahrOverride?: any) => {
		try {
			let appCopy = cloneDeep(application);
			const UID = generateUniqueId();
			const gruppierungID = generateUniqueId();

			const index = get(appCopy, P.Antrag_Kostenpositions.path, []).length;
			const details = halbjahrOverride ?? halbJahrDetails;

			const data = {
				[getFieldNamefromDataPath(KP_P.Antrag.path)]: get(appCopy, 'id'),
				[getFieldNamefromDataPath(KP_P.Allgemein_Kostenart.path)]: KOSTEN_ARTEN.Personalkosten,
				[getFieldNamefromDataPath(KP_P.Allgemein_Position_ID.path)]: UID,
				[getFieldNamefromDataPath(KP_P.Allgemein_Gruppierung_ID.path)]: gruppierungID,
				[getFieldNamefromDataPath(KP_P.Beantragte_Foerderung_Uwo.path)]: get(details, 'unterrichtswochen', 0),
				[getFieldNamefromDataPath(KP_P.Beantragte_Foerderung_Honorarsatz.path)]: get(details, 'honorar', 0),
			};

			set(appCopy, addIndexesToPath(P.Antrag_Kostenpositions.path, [`${index}`], ['Antrag_Kostenpositions']), { attributes: data });
			saveApplication(false, appCopy);
		} catch (err: any) {
			console.error('ERROR: Creating Personal-Kostenposition ', err);
		}
	};
	const handleAddEinzelPosition = async (gruppierungID: string) => {
		try {
			let appCopy = cloneDeep(application);
			const index = get(appCopy, P.Antrag_Kostenpositions.path, []).length;
			const UID = generateUniqueId();
			const data = {
				[getFieldNamefromDataPath(KP_P.Antrag.path)]: get(appCopy, 'id'),
				[getFieldNamefromDataPath(KP_P.Allgemein_Kostenart.path)]: KOSTEN_ARTEN.Personalkosten,
				[getFieldNamefromDataPath(KP_P.Allgemein_Position_ID.path)]: UID,
				[getFieldNamefromDataPath(KP_P.Allgemein_Gruppierung_ID.path)]: gruppierungID,
				[getFieldNamefromDataPath(KP_P.Allgemein_Foerderprogramm.path)]: null,
				[getFieldNamefromDataPath(KP_P.Beantragte_Foerderung_Uwo.path)]: get(halbJahrDetails, 'unterrichtswochen', 0),
				[getFieldNamefromDataPath(KP_P.Beantragte_Foerderung_Honorarsatz.path)]: get(halbJahrDetails, 'honorar', 0),
			};
			set(appCopy, addIndexesToPath(P.Antrag_Kostenpositions.path, [`${index}`], ['Antrag_Kostenpositions']), { attributes: data });
			saveApplication(false, appCopy);
		} catch (err: any) {
			console.error('ERROR: Creating Personal-Kostenposition Einzel ', err);
		}
	};
	const handleDeleteEinzelPosition = async (gruppierungID: string) => {
		try {
			let appCopy = cloneDeep(application);
			const allKostenPositions = get(application, P.Antrag_Kostenpositions.path, []);
			let filteredPositionNachID = allKostenPositions.filter((ele: any) => get(ele, KP_P.Allgemein_Gruppierung_ID.path, '') === gruppierungID);

			if (isArray(filteredPositionNachID) && filteredPositionNachID.length > 0) {
				const lastPosition_ID = get(filteredPositionNachID[filteredPositionNachID.length - 1], 'id', -1);
				if (lastPosition_ID) {
					let res = await DeleteRecord(APIS.Kostenposition, lastPosition_ID);
					if (res) {
						const newPositionForApp_ARR = allKostenPositions.filter((ele: any) => get(ele, 'id') !== lastPosition_ID);
						set(appCopy, P.Antrag_Kostenpositions.path, newPositionForApp_ARR);
						saveApplication(false, appCopy);
					}
				}
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Einzel Personalkosten ', err);
		}
	};
	const handleDeleteFoerderProgram = async (gruppierungID: string) => {
		try {
			let appCopy = cloneDeep(application);
			const allKostenPositions = get(application, P.Antrag_Kostenpositions.path, []);
			let filteredPositionNachID = allKostenPositions.filter((ele: any) => get(ele, KP_P.Allgemein_Gruppierung_ID.path, '') === gruppierungID);
			let deletedPositions: number[] = [];

			if (isArray(filteredPositionNachID) && filteredPositionNachID.length > 0) {
				for (const eachElement of filteredPositionNachID) {
					let res = await DeleteRecord(APIS.Kostenposition, get(eachElement, 'id', -1));
					if (res) {
						deletedPositions.push(get(eachElement, 'id', -1));
					}
				}
			}

			if (deletedPositions.length > 0) {
				const newPositionForApp_ARR = allKostenPositions.filter((ele: any) => !deletedPositions.includes(get(ele, 'id')));
				set(appCopy, P.Antrag_Kostenpositions.path, newPositionForApp_ARR);
				saveApplication(false, appCopy);
			}
		} catch (err: any) {
			console.error('ERROR: Deleting Foerderprogramm ', err);
		}
	};

	useEffect(() => {
		const fetch = async () => {
			try {
				let programmRes = await GetRecords(APIS.FörderProgramme);
				let sparteRes = await GetRecords(APIS.Sparte, [getFieldNamefromDataPath(SP_P.Foerderprogramm.path)]);
				const schuleFilter = {
					// [getFieldNamefromDataPath(UN_P.Geschaeftsbezogene_Daten_Rolle.path)]: UNTERNEHMENS_ROLLE.Grundschlue,
					[getFieldNamefromDataPath(UN_P.MKS.path)]: get(application, `${P.MKS.path}.id`),
				};
				let schuleRes = await GetRecords(APIS.Organisation, [], schuleFilter);
				if (programmRes) setAllFoerderProgramm(removeDataKeys(removeNullDeep(get(programmRes, 'data', []))));
				if (sparteRes) setAllSparte(removeDataKeys(removeNullDeep(get(sparteRes, 'data', []))));
				if (schuleRes) setAllGrundSchule(removeDataKeys(removeNullDeep(get(schuleRes, 'data', []))));
			} catch (err: any) {
				console.error('ERROR: Fetcching Förderprogramme ', err);
			}
		};
		fetch();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<>
			<ApplicationHeading title='Kosten' description={PLATZHALTER_TEXT} />
			<Stack direction={{ xs: 'column', md: 'row' }} justifyContent={'space-between'} alignItems={'center'}>
				<MUIToolTip title={PROGRAMM_MAX_ERLAUBT_TEXT} showTootip={isAddProgrammDisabled}>
					<Button onClick={() => handleAddPersonalKosten()} endIcon={<AddIcon />} disabled={readOnly || isAddProgrammDisabled}>
						Förderprogramm hinzufügen
					</Button>
				</MUIToolTip>
			</Stack>

			{/* eslint-disable-next-line array-callback-return */}
			{Object.keys(groupedData).map((gruppierungID: any, index: number) => {
				if (gruppierungID !== undefined) {
					const anzahlPositionen = get(groupedData, gruppierungID, []).length;
					const isAddEinzelPositionDisabled = readOnly || anzahlPositionen >= 50;
					return (
						<Box
							key={`${gruppierungID + index}`}
							sx={{
								mt: '1rem',
								...(printMode ?
									shouldUseVerticalPrintLayout ?
										index === 0 ?
											{ border: 'none', breakInside: 'auto', pageBreakInside: 'auto' }
										:	{ border: 'none', breakInside: 'avoid-page', pageBreakInside: 'avoid' }
									:	{ border: 'none', breakInside: 'avoid-page', pageBreakInside: 'avoid' }
								:	{ border: '2px solid' }),
								// border: '2px solid',
								padding: '1rem',
							}}
						>
							<Box
								sx={
									printMode && shouldUseVerticalPrintLayout ?
										index === 0 ?
											{ breakInside: 'auto', pageBreakInside: 'auto' }
										:	{ breakInside: 'avoid-page', pageBreakInside: 'avoid' }
									:	{}
								}
							>
								{printMode && (
									<Typography variant='body2' sx={{ mb: 1, fontWeight: 'bold', breakAfter: 'avoid-page', pageBreakAfter: 'avoid' }}>
										Förderprogramm
									</Typography>
								)}
								<CreateForm
									formStruc={formStrucProgramm(application, gruppierungID, allFoerderProgramms)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									printMode={printMode}
									api={APIS.Kostenposition}
								/>
								<CreateTable
									formStruc={formStrucTabelle(application, gruppierungID, allSparte, allGrundSchule, printMode)}
									formValues={application}
									updateFormValuesFunc={setApplicationProperty}
									readOnly={readOnly}
									api={APIS.Kostenposition}
									columnsLabel={printMode ? columnsLabelForPrint(application) : columnsLabel(application)}
									size='small'
									printMode={printMode}
									// tableScrollLeft={tableScrollLeft}
									// setTableScrollLeft={setTableScrollLeft as React.Dispatch<React.SetStateAction<number>>}
								/>
							</Box>
							<Stack direction={{ xs: 'column', md: 'row' }} alignItems={'right'} spacing={2} sx={{ mt: '1rem' }}>
								<MUIToolTip title={EINZELPOSITION_MAX_ERLAUBT_TEXT} showTootip={isAddEinzelPositionDisabled}>
									<Button
										onClick={() => handleAddEinzelPosition(gruppierungID)}
										endIcon={<AddIcon />}
										disabled={readOnly || isAddEinzelPositionDisabled}
									>
										Einzelposition hinzufügen
									</Button>
								</MUIToolTip>
								<Button onClick={() => handleDeleteEinzelPosition(gruppierungID)} endIcon={<Delete />} disabled={readOnly}>
									Letzte Position löschen
								</Button>
								<Button onClick={() => handleDeleteFoerderProgram(gruppierungID)} endIcon={<Delete />} disabled={readOnly}>
									Diesen Block löschen
								</Button>

								<Box sx={printMode ? { fontWeight: 'bold', textAlign: 'right', flexGrow: 1 } : { flexGrow: 1 }} />
								{Object.entries(FoerderSumme).map(([groupKey, value]: [string, { foerderprogramm: string; summe: number }]) => {
									const v = value as { foerderprogramm: string; summe: number };
									if (groupKey !== gruppierungID) return null; // Only show the sum for the current group
									if (v.summe === 0) return null;
									return (
										<Typography align='right' key={groupKey} sx={printMode ? { fontWeight: 'bold' } : {}}>
											{' '}
											beantragte Förderung Summe: {DisplayEuro(v.summe)}
										</Typography>
									);
								})}
							</Stack>
						</Box>
					);
				}
			})}
		</>
	);
};

export default PersonalKosten;

const formStrucProgramm = (application: any, gruppierungID: string, allFoerderProgramms?: any[]) => {
	let struc: FormStrucItem[] = [];
	const allFoerderProgrammsOptions = allFoerderProgramms || [];
	const allKostenPositions = get(application, P.Antrag_Kostenpositions.path, []);

	// Das ist nur einen Test, um zu verhindern, dass die gleiche Förderprogramm nicht in jede Gruppe ausgewählt werden kann
	// muss noch erweitert werden, falls unbedingt notwending
	// const positionWithProgrammIds: number[] = allKostenPositions.map((eachKP: any) =>
	// 	get(eachKP, KP_P.Allgemein_Kostenart.path) === KOSTEN_ARTEN.Personalkosten && get(eachKP, KP_P.Allgemein_Foerderprogramm.path) ?
	// 		getIDFromResponseOption(get(eachKP, KP_P.Allgemein_Foerderprogramm.path))
	// 	:	0
	// );
	// const filteredProgrammOptions = allFoerderProgrammsOptions.filter((eachFP: any) => !positionWithProgrammIds.includes(get(eachFP, 'id')));

	let foundOneElement: boolean = false;
	for (let i = 0; i < allKostenPositions.length; i++) {
		const eachKostenPosition = allKostenPositions[i];

		if (!foundOneElement) {
			if (get(eachKostenPosition, KP_P.Allgemein_Gruppierung_ID.path) === gruppierungID) {
				foundOneElement = true;
				struc = [
					...mapKosten(
						[
							{
								fields: [
									{
										...P.Antrag_Kostenpositions.schema.attributes.Allgemein_Foerderprogramm,
										type: FORM_FIELDS_TYPES.singleSelect,
										options: getOptionsFromRes(allFoerderProgrammsOptions, getFieldNamefromDataPath(FP_P.Allgemein_Titel.path)),
									},
								],
							},
						],
						i,
					),
				];
			}
		}
	}

	return struc;
};

const columnsLabel = (application: any, _sachKostenAvailable?: boolean) => {
	let labels = [
		{ label: 'Kooperationspartner', width: '11rem', maxWidth: '11rem' },
		{ label: 'Sparte', width: '8rem', maxWidth: '8rem' },
		{ label: 'Lehrkraft', width: '11rem', maxWidth: '11rem' },
		{ label: 'Anstellung' },
		{ label: 'Klassen / Gruppen', maxWidth: '6rem', minWidth: '6rem', width: '6rem' },
		{ label: 'Ustd./ Woche', maxWidth: '6rem', minWidth: '6rem', width: '6rem' },
		{ label: 'UWo', maxWidth: '6rem', minWidth: '6rem', width: '6rem' },
		{ label: 'Honorarsatz (€)', maxWidth: '8rem', minWidth: '8rem', width: '8rem' },
		{ label: 'beantragte Personalkosten', maxWidth: '8rem', minWidth: '8rem', width: '8rem' },
		{ label: 'Sachkosten', maxWidth: '6rem', minWidth: '6rem', width: '6rem' },
		{ label: 'beantragte Förderung', maxWidth: '10rem', minWidth: '10rem', width: '10rem' },
	];
	if (BEWILLIGTE_SPALTE_ANZEIGEN.includes(get(application, P.Status_Status.path))) labels.push({ label: 'bewilligte Förderung' });
	return labels;
};
const columnsLabelForPrint = (_application: any, _sachKostenAvailable?: boolean) => {
	let labels = [
		{ label: 'Kooperationspartner', width: '11rem', maxWidth: '11rem' },
		{ label: 'Sparte', width: '8rem', maxWidth: '8rem' },
		{ label: 'Lehrkraft', width: '9rem', maxWidth: '9rem' },
		{ label: 'Anstellung', width: '4rem', maxWidth: '4rem' },
		{ label: 'Klassen', maxWidth: '3rem', minWidth: '3rem', width: '3rem' },
		{ label: 'Ustd.', maxWidth: '3rem', minWidth: '3rem', width: 'rem' },
		{ label: 'UWo', maxWidth: '2rem', minWidth: '2rem', width: '2rem' },
		{ label: 'Honorar', maxWidth: '4rem', minWidth: '4rem', width: '4rem' },
		{ label: 'Personalkosten', maxWidth: '6rem', minWidth: '6rem', width: '6rem' },
		{ label: 'Sachkosten', maxWidth: '6rem', minWidth: '6rem', width: '6rem' },
		{ label: 'Förderung', maxWidth: '7rem', minWidth: '7rem', width: '7rem' },
	];
	// if (BEWILLIGTE_SPALTE_ANZEIGEN.includes(get(application, P.Status_Status.path))) labels.push({ label: 'bewilligte Förderung' });
	return labels;
};

const formStrucTabelle = (application: any, gruppierungID: string, allSparte?: any[], allGrundSchule?: any[], printMode: boolean = false) => {
	let struc: FormStrucItem[] = [];
	const allSparteOption = allSparte || [];
	const allGrundSchuleOption = allGrundSchule || [];
	const allKostenPositions = get(application, P.Antrag_Kostenpositions.path, []);

	for (let i = 0; i < allKostenPositions.length; i++) {
		const eachPosition = allKostenPositions[i];
		let tempStruc: FormStrucItem[] = [];

		if (
			get(eachPosition, KP_P.Allgemein_Gruppierung_ID.path, '') === gruppierungID &&
			get(eachPosition, KP_P.Allgemein_Kostenart.path, '') === KOSTEN_ARTEN.Personalkosten
		) {
			const selectedProgrammID = getIDFromResponseOption(get(eachPosition, KP_P.Allgemein_Foerderprogramm.path));
			const sparteOption_Filtered = allSparteOption.filter((ele: any) => get(ele, `${SP_P.Foerderprogramm.path}.id`) === selectedProgrammID);

			tempStruc.push({
				fields: [
					{
						...P.Antrag_Kostenpositions.schema.attributes.Allgemein_Schule,
						type: FORM_FIELDS_TYPES.singleSelect,
						options: getOptionsFromRes(
							allGrundSchuleOption,
							getFieldNamefromDataPath(UN_P.Geschaeftsbezogene_Daten_Name_Der_Organisation.path),
						),
					},
					{
						...P.Antrag_Kostenpositions.schema.attributes.Allgemein_Sparte,
						type: FORM_FIELDS_TYPES.singleSelect,
						options: getOptionsFromRes(sparteOption_Filtered, getFieldNamefromDataPath(SP_P.Allgemein_Bezeichnung.path)),
					},
					{
						...P.Antrag_Kostenpositions.schema.attributes.Allgemein_Lehrkraft_MKS,
						allowLettersOnly: true,
						validation: [{ validateFunction: Required }],
					},
					P.Antrag_Kostenpositions.schema.attributes.Beantragte_Foerderung_Anstellung,
					P.Antrag_Kostenpositions.schema.attributes.Beantragte_Foerderung_Klassen,
					P.Antrag_Kostenpositions.schema.attributes.Beantragte_Foerderung_Ustd_Woche,
					{ ...P.Antrag_Kostenpositions.schema.attributes.Beantragte_Foerderung_Uwo, disabled: true },
					{ ...P.Antrag_Kostenpositions.schema.attributes.Beantragte_Foerderung_Honorarsatz, disabled: true },
					{ ...P.Antrag_Kostenpositions.schema.attributes.Beantragte_Foerderung_beantragte_Personalkosten, disabled: true },
					{ ...P.Antrag_Kostenpositions.schema.attributes.Beantragte_Foerderung_Sachkosten, disabled: true },
					{ ...P.Antrag_Kostenpositions.schema.attributes.Beantragte_Foerderung_beantragte_Foerderung, format: 'currency', disabled: true },
					...(BEWILLIGTE_SPALTE_ANZEIGEN.includes(get(application, P.Status_Status.path)) && !printMode ?
						[{ ...P.Antrag_Kostenpositions.schema.attributes.Bewilligte_Foerderung_bewilligte_Foerderung, format: 'currency', disabled: true }]
					:	[]),
				],
			});

			struc.push(...mapKosten(tempStruc, i));
		}
	}

	return struc;
};

const mapKosten = (formStruc: any[], index: number) => {
	let result = formStruc.map((row) => {
		return {
			...row,
			fields: (get(row, 'fields', []) as Array<any>).map((value) => {
				let pathWithoutIndex = get(value, 'path', '') as string;
				return {
					...value,
					path:
						pathWithoutIndex.slice(0, P.Antrag_Kostenpositions.path.length) +
						`.${index}` +
						pathWithoutIndex.slice(P.Antrag_Kostenpositions.path.length),
				};
			}),
		};
	});
	return result;
};

const getGroupedData = (application: any) => {
	const allKostenPositions = get(application, P.Antrag_Kostenpositions.path, []);
	// eslint-disable-next-line array-callback-return
	const groupedData = allKostenPositions.reduce((acc: any, item: any) => {
		const key = get(item, KP_P.Allgemein_Gruppierung_ID.path, '').trim();

		if (get(item, KP_P.Allgemein_Kostenart.path, '') === KOSTEN_ARTEN.Personalkosten) {
			if (key) {
				if (!acc[key]) {
					acc[key] = [];
				}
				acc[key].push(item);
			}
		}
		return acc;
	}, {});
	return groupedData;
};

const getFoerderSumme = (groupedData: any) => {
	let foerderSumme: any = {};
	Object.keys(groupedData).forEach((key) => {
		const summe = groupedData[key].reduce(
			(total: number, item: any) => total + (get(item, KP_P.Beantragte_Foerderung_beantragte_Foerderung.path, 0) || 0),
			0,
		);
		const fp = get(groupedData[key][0], KP_P.Allgemein_Foerderprogramm.schema.attributes.Allgemein_Titel.path, '');
		foerderSumme[key] = { foerderprogramm: fp, summe };
	});
	return foerderSumme;
};

export const getFormStrucPersonalKosten = (application: any) => {
	const groupedData = getGroupedData(application);
	let formstruc: FormStrucItem[] = [];
	Object.keys(groupedData).map((gruppierungID: any, _index: number) => {
		if (gruppierungID !== undefined) {
			const programm = formStrucProgramm(application, gruppierungID, []);
			const tabelle = formStrucTabelle(application, gruppierungID, [], []);

			// Both return arrays, so spread them into formstruc
			formstruc.push(...programm, ...tabelle);
			// formstruc.push(concat(formStrucProgramm(application, gruppierungID, [])[0], formStrucTabelle(application, gruppierungID, [], [])[0]));
		}
	});

	return formstruc;
};
